<template>
  <tr>
    <td>{{ location.localCode }}</td>
    <td class="has-text-weight-bold text-truncate" :title="location.locationName">
      {{location.locationName}}
    </td>
    <td>{{ location.baseYear }}</td>
    <td class="border-right">
      {{ typeof location.siteCount === 'number' ? location.siteCount : '--' }}
    </td>
    <td>{{ location.baseYearTotalIdentified }}</td>
    <td class="border-right">{{ location.baseYearTotalEnrolled }}</td>
    <td class="nowrap">
      <span
        :class="{ 'hidden': location.action !== 'C' }"
        data-toggle="tooltip"
        :title="infoTitle">
        <font-awesome-icon
          :icon="['fas', 'info-square']"
          size="lg" class="mr-1 has-text-grey-light"/>
      </span>
      {{ typeof location.baseYearISP === 'number' ? location.baseYearISP.toFixed(2) : '--' }}
      <es-trend-indicator
        :prevISP="location.prevISP"
        :currentISP="location.baseYearISP"/>
    </td>
    <td>
      {{ typeof location.baseYearCalculatedISP === 'number' ? location.baseYearCalculatedISP.toFixed(2) : '--' }}
    </td>
    <td>{{ location.participationPrevNew }}</td>
    <td class="border-right nowrap">
      <span
        class="text-nowrap has-text-weight-bold"
        title="Submitted"
        v-if="location.electionStatus === 'S'">
        <font-awesome-icon :icon="['fas', 'dot-circle']" class="has-text-info fa-lg"/>
        Submitted
      </span>
      <span
        class="text-nowrap has-text-weight-bold"
        title="Pending"
        v-if="location.electionStatus === 'P'">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="has-text-warning fa-lg"/>
        Pending
      </span>
      <span
        class="text-nowrap has-text-weight-bold"
        title="Accepted"
        v-if="location.electionStatus === 'A'">
        <font-awesome-icon :icon="['fas', 'check-circle']" class="has-text-success fa-lg"/>
        Accepted
      </span>
      <span
        class="text-nowrap has-text-weight-bold"
        title="Rejected"
        v-if="location.electionStatus === 'R'">
        <font-awesome-icon :icon="['fas', 'ban']" class="has-text-danger fa-lg"/>
        Rejected
      </span>
    </td>
    <td class="border-right">
      <cep-manager-decision-status
        :status="location.participationDecision === 'No' ? 'N' : location.decision"
        :variant="'outlined'"/>
    </td>
    <td>
      <div class="is-flex flex-align--center flex-justify--start">
        <div class="is-flex flex-align--center">
          <div class="action-padding"
            v-if="
              isStateUser
              && schoolYear === currentSchoolYear
              && hasModule('DRMCH:CEP:ELECTIONS:APPROVE')">
            <a title="Rejected"
              :class="{ 'disabled': checkRejectStatus}"
              @click.prevent="rejectOrApprove('reject');">
              <font-awesome-icon :icon="['fas', 'ban']" size="lg"/>
            </a>
          </div>
          <div
            class="action-padding"
            v-if="
              isStateUser
              && schoolYear === currentSchoolYear
              && hasModule('DRMCH:CEP:ELECTIONS:APPROVE')">
            <a title="Accepted"
              :class="{ 'disabled': checkApprovalStatus }"
              @click.prevent="rejectOrApprove('approve');">
              <font-awesome-icon :icon="['fas', 'check']" size="lg"/>
            </a>
          </div>
          <div class="action-padding" v-if="location.clusterId && !this.cepSummary.districtWide">
            <router-link
              :to="{
                path: `/directMatch/cep/electionsSubmission/${clusterOrStateId}`,
                query: {
                  locationGroupId,
                  schoolYear,
                  cluster: true,
                },
              }"
              title="View details">
              <font-awesome-icon
                :icon="['fas', 'angle-double-right']"
                size="2x"
                title="View Details"/>
            </router-link>
          </div>
        </div>
      </div>
    </td>
  </tr>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
  faBan,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import EsTrendIndicator from '@/components/EsTrendIndicator.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';
import CepManagerDecisionStatus from '@/components/CepManagerDecisionStatus.vue';

library.add(
  faAngleDoubleRight,
  faBan,
  faCheck,
);

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsTrendIndicator,
    CepManagerDecisionStatus,
  },
  props: {
    cepSummary: {
      type: Object,
    },
    electionDate: {
      type: Boolean,
    },
    location: {
      type: Object,
    },
    locationGroupId: {
      type: [String, Number],
    },
    schoolYear: {
      type: Number,
    },
  },
  computed: {
    ...mapGetters({
      isStateUser: 'CEPElectionApprovalUser',
      properties: 'propertiesModule/properties',
    }),
    checkApprovalStatus() {
      return (
        this.electionDate
        || this.location.electionStatus === 'A'
        || this.location.electionStatus === 'P'
        || this.cepSummary.electionStatus === 'O'
      );
    },
    checkRejectStatus() {
      return (
        this.electionDate
        || this.location.electionStatus === 'R'
        || this.location.electionStatus === 'P'
        || this.cepSummary.electionStatus === 'O'
      );
    },
    clusterOrStateId() {
      return this.location.clusterId || this.location.stateLocationId;
    },
    currentSchoolYear() {
      return Number(this.$store.state.propertiesModule.properties['CEP-CEP_CURRENT_SCHOOL_YEAR']);
    },
    infoTitle() {
      return `ISP Base Year: ${typeof this.location.baseYearISP === 'number' ? this.location.baseYearISP.toFixed(2) : '--'}%\nCalculated ISP Base Year: ${typeof this.location.baseYearCalculatedISP === 'number' ? this.location.baseYearCalculatedISP.toFixed(2) : '--'}%`;
    },
    isCluster() {
      return !!this.location.clusterId;
    },
  },
  methods: {
    async rejectOrApprove(action) {
      try {
        const queryParam = this.isCluster ? 'clusterId' : 'schoolId';
        const url = `cep/${this.locationGroupId}/elections/${action}?${queryParam}=${this.clusterOrStateId}`;
        const res = await axios.put(url, {});
        this.showNotification('success',
          [`Elections data is
            ${res.data.clusterAggregate.approvalStatus === 'R' ? 'rejected' : 'accepted'}`]);
      } catch (error) {
        const { status } = error.response;
        let apiMsg = '';
        if (status === 500) {
          apiMsg = 'Network Error';
        } else if (status === 400) {
          apiMsg = `There was a problem approving or rejecting school elections. ${error.response.data.message}`;
        } else {
          apiMsg = 'There was an issue. Please try again or contact your administrator.';
        }
        this.showNotification('danger', [apiMsg]);
      } finally {
        this.$emit('reload');
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .text-truncate
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap
  .border-right
    border-right: 1px solid #dee2e6
  .action-padding
    padding-left: 0.5rem
  a.disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  .table tbody tr td
    padding: .35rem 0.75rem
  .hidden
    visibility: hidden
</style>
